import cx from 'classnames';
import { useRef } from 'react';
import type { VoidFunctionComponent } from 'react';

import { plural } from '../../../../shared/helpers/plural';
import { useListFossabotPRs } from '../../../hooks/api/fossabot/useListFossabotPRs';
import Button, { BUTTON_TYPE } from '../../generic/Button';
import { icons } from '../../generic/Icon';
import List from '../../generic/List';
import type { FossabotPRSort, FossabotPRVcsState } from '../../../../shared/fossabot/common';

import { FossabotPrRow, getFossabotPrKey } from './FossabotPrRow';
import { FossabotPrsTableHeader } from './FossabotPrsTableHeader';

import styles from './FossabotPrsTable.module.less';

/** Placeholder rows shown while the first page is in flight. */
const LOADING_ROW_KEYS = ['first', 'second', 'third'];

/** Shown when the project has no PRs at all. */
const EMPTY_COPY = 'No pull requests';

/**
 * Shown when the project does have PRs but none match what is being filtered on.
 * Worded for filters rather than for the search alone, so the state and sort
 * filters landing next to the search need no second wording.
 */
const FILTERED_EMPTY_COPY = 'No pull requests match your filters';

/** Shown when the list request failed, so an empty list is not a real answer. */
const ERROR_COPY = 'Unable to load pull requests';

/** The button that appends the next page of rows. */
const LOAD_MORE_COPY = 'Load More';

type Props = {
  /**
   * The project locator, without the revision ID.
   * @example `custom+17/cool-project`
   */
  projectLocator: string;
  /** Server-side title search, straight off the URL. Empty means no filter. */
  search: string;
  /** The VCS state being filtered on, straight off the URL. Undefined is All. */
  state?: FossabotPRVcsState;
  /** Called by the header chips with the chosen state, or undefined for All. */
  onStateChange: (state?: FossabotPRVcsState) => void;
  /**
   * The order to list the rows in. Always set, even when the URL carries no sort
   * param, so the request never leaves the ordering to the fossabot subgraph
   * while the header names one.
   */
  sort: FossabotPRSort;
  /** Called by the header's sort control with the order that was picked. */
  onSortChange: (sort: FossabotPRSort) => void;
};

/**
 * The list of a project's fossabot PRs, one row each, and the empty, loading and
 * failed states that stand in for the rows.
 *
 * The rows are fetched here rather than passed down, matching how the stat cards
 * above fetch their own counts, so this stays behind the page's own gates.
 */
export const FossabotPrsTable: VoidFunctionComponent<Props> = ({
  projectLocator,
  search,
  state,
  onStateChange,
  sort,
  onSortChange,
}) => {
  const {
    data,
    isLoading,
    isFetching,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useListFossabotPRs({
    projectLocator,
    state,
    search,
    sort,
  });

  const prs = data?.pages.flatMap((page) => page.nodes) ?? [];
  /*
   * Every filter is part of the query key, and the list keeps the previous key's
   * rows while the new ones load, so a chip click leaves rows on screen that the
   * chip it just lit up says are filtered out. `isLoading` stays false throughout
   * that window, so it is the only signal the swap is still coming.
   *
   * A next-page fetch leaves `isFetching` true as well, but it appends to the rows
   * on screen rather than replacing them, so it must not fade them out. The list
   * is still busy while it runs, which `aria-busy` reports separately below.
   */
  const isRefreshing = isFetching && !isLoading && !isFetchingNextPage;
  /*
   * Disabling the button while its page is in flight would blur it, and a browser
   * moves focus to the body when that happens, so a keyboard user would be thrown
   * back to the top of the document on every page they asked for. The guard sits
   * here instead, and the spinner on the button carries the wait.
   *
   * isFetchingNextPage only updates once React re-renders with the hook's next
   * result, so two clicks inside that window would both read it as false and both
   * call fetchNextPage. The ref closes that window: it is set before the call and
   * cleared once the fetch settles, while isFetchingNextPage still covers a fetch
   * started some other way.
   */
  const isFetchingNextPageRef = useRef(false);
  const onLoadMore = async () => {
    if (isFetchingNextPage || isFetchingNextPageRef.current) {
      return;
    }

    isFetchingNextPageRef.current = true;
    try {
      await fetchNextPage();
    } finally {
      isFetchingNextPageRef.current = false;
    }
  };
  /*
   * A filter that matched nothing is not a project with no PRs, and telling
   * someone their project has none while their filters are on is a lie they can
   * only get out of by guessing.
   */
  const emptyCopy = search || state ? FILTERED_EMPTY_COPY : EMPTY_COPY;

  return (
    /*
     * The skeleton rows are wordless SVGs, so without aria-busy a screen reader
     * hears silence while the first page loads, and the stale rows a filter
     * change leaves behind say nothing at all. List takes no arbitrary props,
     * hence the wrapper. Matches the stat cards above.
     */
    <div aria-busy={isLoading || isRefreshing || isFetchingNextPage}>
      <List className={cx(styles.table, { [styles.refreshing]: isRefreshing })}>
        <FossabotPrsTableHeader
          projectLocator={projectLocator}
          state={state}
          onStateChange={onStateChange}
          sort={sort}
          onSortChange={onSortChange}
        />
        <List.Body>
          {isLoading && LOADING_ROW_KEYS.map((key) => <List.RowLoading key={key} isInteractive={false} />)}
          {/*
           * A failed request is not a project with no PRs: `data` is undefined
           * either way, so the two have to be told apart before the empty message
           * claims there is nothing to show.
           *
           * The message replaces the skeletons after the fact, so it needs a live
           * region of its own to be announced at all; a failure is the more urgent
           * of the two and gets the assertive role.
           */}
          {!isLoading && prs.length === 0 && (
            <div className={styles.empty} role={isError ? 'alert' : 'status'}>
              {isError ? ERROR_COPY : emptyCopy}
            </div>
          )}
          {!isLoading && prs.map((pr) => <FossabotPrRow key={getFossabotPrKey(pr)} pr={pr} />)}
          {/*
           * Inside the body rather than under the list, so the button sits on the
           * card with the rows it extends and the last row's divider separates the
           * two. Undefined until the first page lands, which keeps it off screen
           * while the skeletons are up and after a failed request.
           */}
          {hasNextPage && (
            <div className={styles.loadMore}>
              {/*
               * A failed next page keeps the pages already fetched, so the message
               * above stays skipped and the rows simply stop arriving with nothing
               * said. It is reported here instead, beside the button that still
               * offers the retry.
               */}
              {isError && prs.length > 0 && (
                <span className={styles.loadMoreError} role="alert">
                  {ERROR_COPY}
                </span>
              )}
              <Button
                type={BUTTON_TYPE.OUTLINE}
                onClick={onLoadMore}
                icon={isFetchingNextPage ? icons.spinner : undefined}
                iconHasSpin={isFetchingNextPage}
              >
                {LOAD_MORE_COPY}
              </Button>
            </div>
          )}
        </List.Body>
      </List>
      {/*
       * Searching never moves focus, so on its own the rows swap under a screen
       * reader with nothing said. The count answers a search the same way the
       * messages above answer the empty and the failed cases.
       *
       * Outside the list rather than in the body: the list rounds off its last row
       * and drops that row's divider through `:last-child`, which an extra element
       * after the rows would take for itself.
       */}
      {!isLoading && prs.length > 0 && (
        <div className="sr-only" role="status">
          {`Showing ${prs.length} ${plural(prs.length, 'pull request')}`}
        </div>
      )}
    </div>
  );
};
